import { Navigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../lang";
import { CATEGORIES, MACROS, productsInCategory, groupedProductsInCategory, subCategoriesOf, getProductPills, categoryTrail } from "../catalog";
import Tile from "../components/Tile";
import CardGrid from "../components/CardGrid";
import Breadcrumb from "../components/Breadcrumb";
import { familyOf } from "../compare";

export default function Category() {
  const { catSlug } = useParams();
  const { t, lang } = useLang();

  // Macro category (architettonici / pergole)
  const macro = MACROS[catSlug];
  const cat = CATEGORIES[catSlug];

  if (!macro && !cat) return <Navigate to="/prodotti" replace />;

  if (macro) {
    // Show intermediate: list all sub-cats and (if leaf-of-leaf) their products
    const subs = macro.children.map((s) => CATEGORIES[s]).filter(Boolean);
    return (
      <main className="min-h-screen bg-[#f5f5f7] pt-20" data-testid={`category-page-${catSlug}`}>
        <div className="max-w-[1200px] mx-auto px-6 pt-8">
          <Breadcrumb items={[
            { label: "Home", to: "/" },
            { label: t.productsEyebrow, to: "/prodotti" },
            { label: macro[lang].title },
          ]} />
        </div>

        <section className="pt-8 pb-6">
          <div className="max-w-[1200px] mx-auto px-6 text-center">
            <h1 className="text-[36px] md:text-[56px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
              {macro[lang].title}
            </h1>
            <p className="mt-4 text-[17px] text-[#1d1d1f]/80 max-w-[720px] mx-auto">{macro[lang].sub}</p>
          </div>
        </section>

        <section className="pb-24">
          <div className="max-w-[1200px] mx-auto px-6">
            <CardGrid testId="subcat-grid">
            {subs.map((c, i) => (
              <Tile
                key={c.slug}
                to={`/prodotti/${c.slug}`}
                testId={`subcat-${c.slug}`}
                image={c.image}
                title={c[lang].title}
                subtitle={c[lang].sub}
                variant="card"
                index={i}
              />
            ))}
            </CardGrid>
          </div>
        </section>
      </main>
    );
  }

  // Sub-category
  if (cat.isBranch) {
    // Intermediate branch (alluminio / pvc) — show its own sub-categories
    const subs = subCategoriesOf(catSlug);
    return (
      <main className="min-h-screen bg-[#f5f5f7] pt-20" data-testid={`category-page-${catSlug}`}>
        <div className="max-w-[1200px] mx-auto px-6 pt-8">
          <Breadcrumb items={[
            { label: "Home", to: "/" },
            { label: t.productsEyebrow, to: "/prodotti" },
            MACROS[cat.macroSlug || cat.parent] ? { label: MACROS[cat.macroSlug || cat.parent][lang].title, to: `/prodotti/${cat.macroSlug || cat.parent}` } : null,
            ...categoryTrail(cat.parent).map((c) => ({ label: c[lang].title, to: `/prodotti/${c.slug}` })),
            { label: cat[lang].title },
          ].filter(Boolean)} />
        </div>

        <section className="pt-8 pb-6">
          <div className="max-w-[1200px] mx-auto px-6 text-center">
            <h1 className="text-[36px] md:text-[56px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
              {cat[lang].title}
            </h1>
            <p className="mt-4 text-[17px] text-[#1d1d1f]/80 max-w-[720px] mx-auto">{cat[lang].sub}</p>
          </div>
        </section>

        <section className="pb-24">
          <div className="max-w-[1200px] mx-auto px-6">
            <CardGrid testId="subcat-grid">
            {subs.map((c, i) => (
              <Tile
                key={c.slug}
                to={`/prodotti/${c.slug}`}
                testId={`subcat-${c.slug}`}
                image={c.image}
                title={c[lang].title}
                subtitle={c[lang].sub}
                variant="card"
                index={i}
              />
            ))}
            </CardGrid>
          </div>
        </section>
      </main>
    );
  }

  // LEAF category — list products (grouped into product lines when defined)
  const products = productsInCategory(catSlug);
  const groups = groupedProductsInCategory(catSlug);
  const parentMacro = MACROS[cat.macroSlug];

  return (
    <main className="min-h-screen bg-[#f5f5f7] pt-20" data-testid={`category-page-${catSlug}`}>
      <div className="max-w-[1200px] mx-auto px-6 pt-8">
        <Breadcrumb items={[
          { label: "Home", to: "/" },
          { label: t.productsEyebrow, to: "/prodotti" },
          parentMacro ? { label: parentMacro[lang].title, to: `/prodotti/${parentMacro.slug}` } : null,
          ...categoryTrail(cat.parent).map((c) => ({ label: c[lang].title, to: `/prodotti/${c.slug}` })),
          { label: cat[lang].title },
        ].filter(Boolean)} />
      </div>

      <section className="pt-8 pb-6">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <h1 className="text-[36px] md:text-[56px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
            {cat[lang].title}
          </h1>
          <p className="mt-4 text-[17px] text-[#1d1d1f]/80 max-w-[720px] mx-auto">{cat[lang].sub}</p>
        </div>
      </section>

      <section className="pb-24">
        {products.length === 0 ? (
          <div className="text-center text-[#6e6e73] py-16">
            {lang === "it" ? "Nessun prodotto disponibile per ora." : "No products available yet."}
          </div>
        ) : groups ? (
          <div className="max-w-[1200px] mx-auto px-6 space-y-16">
            {groups.map((g) => (
              <div key={g.id} data-testid={`product-group-${g.id}`}>
                <div className="mb-8 text-center">
                  <span className="block mx-auto mb-4 h-[3px] w-10 rounded-full bg-[#1D3F94]" aria-hidden="true" />
                  <h2 className="text-base md:text-lg font-semibold tracking-[-0.01em] text-[#1d1d1f]">
                    {g[lang].title}
                  </h2>
                  <p className="mt-1.5 text-[14px] text-[#6e6e73] max-w-[640px] mx-auto">{g[lang].sub}</p>
                </div>
                <CardGrid testId={`product-group-grid-${g.id}`}>
                  {g.items.map((p, i) => (
                    <Tile
                      key={p.slug}
                      to={`/prodotti/p/${p.slug}`}
                      testId={`product-tile-${p.slug}`}
                      image={p.image}
                      title={p[lang].name}
                      subtitle={p[lang].tagline}
                      variant="card"
                      index={i}
                      pills={getProductPills(p, lang)}
                      compareSlug={familyOf(p) ? p.slug : undefined}
                    />
                  ))}
                </CardGrid>
              </div>
            ))}
          </div>
        ) : (
          <div className="max-w-[1200px] mx-auto px-6">
            <CardGrid testId="product-grid">
            {products.map((p, i) => (
              <Tile
                key={p.slug}
                to={`/prodotti/p/${p.slug}`}
                testId={`product-tile-${p.slug}`}
                image={p.image}
                title={p[lang].name}
                subtitle={p[lang].tagline}
                variant="card"
                index={i}
                pills={getProductPills(p, lang)}
                compareSlug={familyOf(p) ? p.slug : undefined}
              />
            ))}
            </CardGrid>
          </div>
        )}

        <div className="mt-14 text-center">
          <Link to="/prodotti" className="a-link" data-testid="back-to-products">
            ‹ {t.backToProducts}
          </Link>
        </div>
      </section>
    </main>
  );
}
